import { escapeHtml, formatPageList, createEntityLink, createEntityLinkWithLabel, sortEntityNames, normalizeName, getByName } from './utils';
import { renderNavigationHeader } from './navigation';

function renderImageGallery(images = []) {
  const validImages = (images || []).filter((img) => img && img.src);
  if (validImages.length === 0) {
    return '';
  }

  let html = '<div class="entity-image-gallery">';
  validImages.forEach((img) => {
    const src = img.src;
    const altText = img.alt || img.caption || '';
    const caption = img.caption || '';

    html += '<figure class="entity-image">';
    html += `<img src="${escapeHtml(src)}" alt="${escapeHtml(altText)}" loading="lazy">`;
    if (caption) {
      html += `<figcaption>${escapeHtml(caption)}</figcaption>`;
    }
    html += '</figure>';
  });
  html += '</div>';

  return html;
}

export function renderLinkedList(title, entityType, names, options = {}) {
  const { sort = false } = options;
  const uniqueNames = [...new Set((names || []).filter(Boolean))];
  if (uniqueNames.length === 0) {
    return '';
  }

  const displayNames = sort ? sortEntityNames(uniqueNames) : uniqueNames;

  let html = `<h4>${title}</h4><ul class="entity-list entity-list-links">`;
  displayNames.forEach(name => {
    html += `<li>${createEntityLink(entityType, name)}</li>`;
  });
  html += '</ul>';
  return html;
}

export function getSortedStoreItems(store) {
  return Object.values(store)
    .filter((item) => item && item.name)
    .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, {
      sensitivity: 'base',
      numeric: true
    }));
}

export function renderBrowseGroup(title, entityType, names, options = {}) {
  const { open = false } = options;
  if (!names || names.length === 0) {
    return '';
  }

  let html = `<details class="browse-group" data-browse-category="${entityType}" ${open ? 'open' : ''}>`;
  html += `<summary>${escapeHtml(title)} <span class="browse-count">(${names.length})</span></summary>`;
  html += '<ul class="entity-list entity-list-links browse-list">';
  names.forEach((item) => {
    const name = item.name;
    const appearances = Array.isArray(item.relatedLocations) ? item.relatedLocations.length : 0;
    const countSuffix = entityType === 'location' || appearances === 1 ? '' : ` (${appearances})`;
    const hasInfo =
      (Array.isArray(item.links) && item.links.length > 0) ||
      (Array.isArray(item.images) && item.images.length > 0);
    html += `<li>${createEntityLinkWithLabel(entityType, name, `${name}${countSuffix}`, { hasInfo })}</li>`;
  });
  html += '</ul></details>';
  return html;
}

export function renderBrowseMenu(infoEl, navigationState, locationsByName, referencedLocationsByName, themesByName, peopleByName) {
  const locationNames = getSortedStoreItems(locationsByName);
  const referencedNames = getSortedStoreItems(referencedLocationsByName);
  const themeNames = getSortedStoreItems(themesByName);
  const personNames = getSortedStoreItems(peopleByName);

  let html = '<h3>Browse Entities</h3>';
  html += '<p class="entity-hint">Open a section and select an item to jump to its details.</p>';
  html += renderBrowseGroup('Locations', 'location', locationNames, { open: navigationState.lastBrowseCategory === 'location' });
  html += renderBrowseGroup('People', 'person', personNames, { open: navigationState.lastBrowseCategory === 'person' });
  html += renderBrowseGroup('Referenced Locations', 'referencedLocation', referencedNames, { open: navigationState.lastBrowseCategory === 'referencedLocation' });
  html += renderBrowseGroup('Themes', 'theme', themeNames, { open: navigationState.lastBrowseCategory === 'theme' });

  infoEl.innerHTML = withNavigationChrome(infoEl, navigationState, html);
}

export function renderInfoLanding(infoEl, navigationState) {
  infoEl.innerHTML = withNavigationChrome(infoEl, navigationState, '<h3>Explore Connections</h3><p>Click a map point to view linked locations, themes, and people.</p><p>This is a prototype for displaying data relating to The Rings of Saturn. The data is incomplete, incorrect and not validated. Copyright Andrew Keedle.</p><p class="entity-hint">Use Browse to open lists of people, themes, and referenced locations.</p>');
}

export function renderFeatureInfo(infoEl, navigationState, feature, options = {}, locationFeaturesByName, referencedFeaturesByName, zoomToLinkedLocations, focusOnFeature, connectionVectorLayer, referencedVectorLayer, isZoomedToLinked) {
  const name = feature.get('name');
  const description = feature.get('description');
  const type = feature.get('type') || 'location';
  const step = feature.get('step');
  const visits = feature.get('visits') || [];
  const title = type === 'location' ? `${step}. ${name}` : name;

  const chapter = feature.get('chapter');
  const pages = feature.get('pages');
  let chapterInfo = '';
  if (chapter) chapterInfo += `Chapter ${chapter}`;
  if (pages && type !== 'referencedLocation') chapterInfo += ` · pp. ${pages}`;

  let html = `<h3>${escapeHtml(title)}</h3>`;
  if (chapterInfo) {
    html += `<p class="chapter-info">${escapeHtml(chapterInfo)}</p>`;
  }
  html += `<p>${escapeHtml(description)}</p>`;

  const links = feature.get('links') || [];
  links.forEach(link => {
    html += `<a href="${link.url}" target="_blank" rel="noopener noreferrer">${escapeHtml(link.text)}</a><br>`;
  });

  const images = feature.get('images') || [];
  html += renderImageGallery(images);

  if (type === 'location' && visits.length > 0) {
    html += '<h4>Appearances</h4><ul class="entity-list">';
    visits.forEach(visit => {
      let visitText = `#${visit.order}`;
      if (visit.chapter) {
        visitText += ` · Chapter ${visit.chapter}`;
      }
      if (visit.pages) {
        visitText += ` · pp. ${visit.pages}`;
      }
      if (visit.note) {
        visitText += ` · ${visit.note}`;
      }
      html += `<li>${escapeHtml(visitText)}</li>`;
    });
    html += '</ul>';
  }

  if (type === 'referencedLocation') {
    const refPages = feature.get('pages') || [];
    if (refPages.length > 0) {
      const formattedPages = formatPageList(refPages);
      const label = /^\d+$/.test(formattedPages) && refPages.length === 1 ? 'p.' : 'pp.';
      html += `<p class="chapter-info">${escapeHtml(`${label} ${formattedPages}`)}</p>`;
    }
  }

  const relatedLocations = feature.get('relatedLocations') || [];
  const relatedReferencedLocations = feature.get('relatedReferencedLocations') || [];
  const relatedThemes = feature.get('relatedThemes') || [];
  const relatedPeople = feature.get('relatedPeople') || [];

  if (type === 'location') {
    const hasRelatedReferencedLocations = relatedReferencedLocations.length > 0;
    if (hasRelatedReferencedLocations) {
      const zoomButtonLabel = isZoomedToLinked ? 'Zoom to Location' : 'Zoom to References';
      html += `<button id="zoom-toggle" class="zoom-toggle-btn">${zoomButtonLabel}</button>`;
    }
    html += renderLinkedList('Related Referenced Locations', 'referencedLocation', relatedReferencedLocations, { sort: true });
    html += renderLinkedList('Related Locations', 'location', relatedLocations);
  } else {
    html += renderLinkedList('Related Locations', 'location', relatedLocations);
    html += renderLinkedList('Related Referenced Locations', 'referencedLocation', relatedReferencedLocations, { sort: true });
  }

  html += renderLinkedList('Themes', 'theme', relatedThemes, { sort: true });
  html += renderLinkedList('People', 'person', relatedPeople, { sort: true });

  infoEl.innerHTML = withNavigationChrome(infoEl, navigationState, html);
}

export function renderEntityInfo(infoEl, navigationState, entity, entityType) {
  let html = `<h3>${escapeHtml(entity.name)}</h3><p>${escapeHtml(entity.description || '')}</p>`;

  const rawPages = Array.isArray(entity.pages)
    ? entity.pages
    : (entity.pages ? [entity.pages] : []);
  if (rawPages.length > 0) {
    const formattedPages = formatPageList(rawPages);
    const hasSingleNumericPage = rawPages.length === 1 && /^\d+$/.test(String(rawPages[0]).trim());
    html += `<p class="chapter-info">${escapeHtml(`${hasSingleNumericPage ? 'p.' : 'pp.'} ${formattedPages}`)}</p>`;
  }

  const entityLinks = entity.links || [];
  entityLinks.forEach(link => {
    const label = link.title || link.text || link.url;
    html += `<a href="${escapeHtml(link.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a><br>`;
  });

  html += renderImageGallery(entity.images || []);

  html += renderLinkedList('Locations', 'location', entity.relatedLocations || []);
  html += renderLinkedList('Referenced Locations', 'referencedLocation', entity.relatedReferencedLocations || [], { sort: true });
  html += renderLinkedList('Themes', 'theme', entity.relatedThemes || [], { sort: true });
  html += renderLinkedList('People', 'person', entity.relatedPeople || [], { sort: true });

  if (entityType === 'theme') {
    html += '<p class="entity-hint">Theme record</p>';
  }

  infoEl.innerHTML = withNavigationChrome(infoEl, navigationState, html);
}

export function withNavigationChrome(infoEl, navigationState, contentHtml) {
  return `${renderNavigationHeader(navigationState)}${contentHtml}`;
}
